import {
  type CreatorsV2Filter,
  makeQueryParams,
  normalizeUsers,
  type UserItem,
} from '@metapic-apps/shared'
import { type Ref, ref, toValue, watch, watchEffect } from 'vue'

import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import { useErrorStore } from '@/stores/error'

/** Callers that want a shorter list pass `size` on the filter. */
const DEFAULT_CREATOR_PAGE_SIZE = 20

export const useGetUserListRequest = (filter: Ref<CreatorsV2Filter>, skip?: boolean) => {
  const data = ref<UserItem[]>([])
  const isLoading = ref(false)
  const error = ref<Error | null>(null)

  const errorStore = useErrorStore()

  const fetchUsers = async (params: URLSearchParams | undefined) => {
    try {
      data.value = []
      isLoading.value = true
      const response = await request.get('/v2/users', { params })
      data.value = normalizeUsers(response.data.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watchEffect(() => {
    if (skip) {
      return
    }

    const query = filter.value.query?.trim()?.toLowerCase()

    const params = makeQueryParams({
      advertiserGroupId: filter.value.advertiserGroup?.id,
      advertiserId: filter.value.advertiser?.id,
      query,
      status: filter.value.status,
      sortBy: query ? 'email:asc' : 'last_active:desc',
      size: String(filter.value.size ?? DEFAULT_CREATOR_PAGE_SIZE),
      include: ['email'],
    })

    if (params) {
      void fetchUsers(params)
    }
  })

  return {
    data,
    isLoading,
    error,
  }
}

export const useGetUsersFromArrayRequest = (
  advertiserGroupId: Ref<number>,
  users: Ref<string[]>,
) => {
  const data = ref<{ matched: UserItem[]; unmatched: string[] }>({ matched: [], unmatched: [] })
  const isLoading = ref(false)
  const error = ref<Error | null>(null)

  const errorStore = useErrorStore()

  const fetchUsers = async (users: string[]) => {
    try {
      data.value = { matched: [], unmatched: [] }
      isLoading.value = true
      const response = await request.post('/v2/match-users', {
        advertiser_group_id: advertiserGroupId.value,
        user_emails_or_ids: users,
      })
      data.value = {
        matched: normalizeUsers(response.data.matched_users),
        unmatched: response.data.unmatched_users,
      }
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(
    () => users,
    () => {
      const usersArray = toValue(users)
      if (usersArray.length) {
        return fetchUsers(usersArray)
      }
    },
    {
      deep: true,
    },
  )

  return {
    data,
    isLoading,
    error,
  }
}
